import { useState, type FormEvent, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { adminErrorMessage, StatusBadge } from "./AdminState";
import { apiJson } from "../lib/apiClient";
import { requestStatuses, statusLabels, type RequestStatus } from "../lib/constants";
import { useApi } from "./useApi";

type RequestDetail = {
  request: {
    id: string;
    request_no: string;
    status: RequestStatus;
    purpose: string;
    description: string;
    city: string;
    internal_owner: string | null;
    next_action: string | null;
    created_at: string;
    updated_at: string;
    closed_at: string | null;
    delete_after: string | null;
    organization_name: string;
    organization_type: string;
    contact_full_name: string;
    contact_title: string;
    mobile: string;
    official_email: string;
    district: string;
    property_type: string;
    location_url: string | null;
  };
  attachments: Array<{
    id: string;
    original_filename: string;
    file_size: number;
    uploaded_at: string;
    delete_after: string | null;
    deleted_at: string | null;
  }>;
  notes: Array<{ id: string; note: string; actor_email: string; created_at: string }>;
  quotations: Array<{ id: string; quotation_no: string; status: string; total_amount: number }>;
  work_orders: Array<{ id: string; work_order_no: string; status: string; assigned_owner: string }>;
  inspections: Array<{ id: string; inspection_date: string; inspector_name: string }>;
  reports: Array<{ id: string; report_no: string; report_type: string; status: string }>;
  invoice_records: Array<{ id: string; total_amount: number; payment_status: string }>;
  status_history: Array<{ id: string; old_status: string; new_status: string; actor_email: string; created_at: string }>;
};

export function AdminRequestDetail() {
  const { id } = useParams();
  const path = `/api/admin/requests/${id}`;
  const { data, error, status, loading, reload } = useApi<RequestDetail>(path);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  async function submitJson(route: string, form: HTMLFormElement) {
    setMessage(null);
    const payload = Object.fromEntries(new FormData(form).entries());
    try {
      await apiJson(route, {
        method: route.endsWith("/status") ? "PATCH" : "POST",
        body: JSON.stringify(payload),
      });
      form.reset();
      setMessage({ type: "success", text: "تم حفظ التغيير." });
      await reload();
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "تعذر حفظ التغيير.",
      });
    }
  }

  if (loading) return <Panel title="تفاصيل الطلب">جاري التحميل...</Panel>;
  if (error || !data?.request) {
    return <Panel title="تفاصيل الطلب">{adminErrorMessage(error || "لم يتم العثور على الطلب.", status)}</Panel>;
  }

  const request = data.request;
  const attachments = data.attachments ?? [];
  const notes = data.notes ?? [];
  const quotations = data.quotations ?? [];
  const workOrders = data.work_orders ?? [];
  const inspections = data.inspections ?? [];
  const reports = data.reports ?? [];
  const invoiceRecords = data.invoice_records ?? [];
  const statusHistory = data.status_history ?? [];

  return (
    <div className="grid gap-6">
      <Panel title={request.request_no}>
        {message ? (
          <p
            className={`mb-4 rounded-md p-3 ${
              message.type === "success" ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900"
            }`}
          >
            {message.text}
          </p>
        ) : null}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-offwhite p-4">
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={request.status} />
            <span className="text-sm font-bold text-ash">
              آخر تحديث: {request.updated_at}
            </span>
          </div>
          <span className="text-sm text-ash">تاريخ الاستلام: {request.created_at}</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Info label="الجهة" value={`${request.organization_name} - ${request.organization_type}`} />
          <Info label="مسؤول التواصل" value={`${request.contact_full_name} - ${request.contact_title}`} />
          <Info label="بيانات التواصل" value={`${request.mobile} - ${request.official_email}`} />
          <Info label="العقار" value={`${request.city} - ${request.district} - ${request.property_type}`} />
          <Info label="الغرض" value={request.purpose} />
          <Info label="الإجراء التالي" value={request.next_action ?? "-"} />
        </div>
        <div className="mt-5 rounded-md bg-sand p-4 leading-8">
          <strong className="block text-charcoal">الوصف المختصر</strong>
          {request.description}
        </div>
      </Panel>

      <Panel title="تحديث الحالة">
        <form
          className="grid gap-4 md:grid-cols-3"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            void submitJson(`${path}/status`, event.currentTarget);
          }}
        >
          <div className="field">
            <label>الحالة</label>
            <select name="status" defaultValue={request.status}>
              {requestStatuses.map((status) => (
                <option key={status} value={status}>
                  {statusLabels[status]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>المسؤول الداخلي</label>
            <input name="internal_owner" defaultValue={request.internal_owner ?? ""} />
          </div>
          <div className="field">
            <label>الإجراء التالي</label>
            <input name="next_action" defaultValue={request.next_action ?? ""} />
          </div>
          <button className="btn btn-primary md:col-span-3" type="submit">
            تحديث الحالة
          </button>
        </form>
      </Panel>

      <Panel title="المرفقات">
        <p className="mb-4 rounded-md bg-sand p-3 text-sm leading-7 text-ash">
          المرفقات محفوظة في تخزين خاص، ولا توجد روابط عامة للملفات.
        </p>
        <div className="grid gap-3">
          {attachments.map((file) => (
            <div key={file.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line p-4">
              <div>
                <strong className="text-charcoal">{file.original_filename}</strong>
                <p className="text-sm text-ash">
                  {(file.file_size / 1024 / 1024).toFixed(2)}MB - {file.uploaded_at} - الحذف: {file.delete_after ?? "-"}
                </p>
              </div>
              <a className="btn btn-outline" href={`/api/admin/files/${file.id}`}>
                تنزيل محمي عبر الإدارة
              </a>
            </div>
          ))}
          {!attachments.length ? <p className="text-ash">لا توجد مرفقات.</p> : null}
        </div>
      </Panel>

      <Panel title="الملاحظات الداخلية">
        <form
          className="mb-5 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void submitJson(`${path}/notes`, event.currentTarget);
          }}
        >
          <div className="field">
            <label>ملاحظة</label>
            <textarea name="note" rows={4} required />
          </div>
          <button className="btn btn-primary" type="submit">إضافة ملاحظة</button>
        </form>
        <div className="grid gap-3">
          {notes.map((note) => (
            <div key={note.id} className="rounded-md border border-line p-4">
              <p className="leading-8">{note.note}</p>
              <span className="text-sm text-ash">{note.actor_email} - {note.created_at}</span>
            </div>
          ))}
          {!notes.length ? <p className="text-sm text-ash">لا توجد ملاحظات داخلية.</p> : null}
        </div>
      </Panel>

      <div className="grid gap-6 xl:grid-cols-2">
        <QuickForm title="عرض سعر" action={`${path}/quotation`} onSubmit={submitJson}>
          <Field name="scope" label="النطاق" textarea />
          <Field name="expected_duration" label="المدة المتوقعة" />
          <Field name="amount_before_vat" label="المبلغ قبل الضريبة" type="number" />
          <Field name="vat_amount" label="الضريبة" type="number" />
          <Field name="total_amount" label="الإجمالي" type="number" />
          <Field name="valid_until" label="تاريخ الصلاحية" type="date" />
          <Field name="terms" label="الشروط" textarea />
        </QuickForm>
        <QuickForm title="أمر عمل" action={`${path}/work-order`} onSubmit={submitJson}>
          <Field name="quotation_id" label="معرف عرض السعر" />
          <Field name="scope" label="النطاق" textarea />
          <Field name="assigned_owner" label="المسؤول" />
          <Field name="target_inspection_date" label="تاريخ المعاينة المستهدف" type="date" />
          <Field name="target_report_date" label="تاريخ التقرير المستهدف" type="date" />
          <Field name="status" label="الحالة" />
          <Field name="internal_notes" label="ملاحظات داخلية" textarea />
        </QuickForm>
        <QuickForm title="معاينة" action={`${path}/inspection`} onSubmit={submitJson}>
          <Field name="inspection_date" label="تاريخ المعاينة" type="date" />
          <Field name="inspector_name" label="اسم المعاين" />
          <Field name="findings_summary" label="ملخص النتائج" textarea />
          <Field name="recommendation" label="التوصية الأولية" textarea />
        </QuickForm>
        <QuickForm title="تقرير" action={`${path}/report`} onSubmit={submitJson}>
          <div className="field">
            <label>نوع التقرير</label>
            <select name="report_type">
              <option value="technical">تقرير تقييم هندسي لأصل عقاري</option>
              <option value="insurance">تقرير تأميني هندسي</option>
              <option value="executive_summary">ملخص تنفيذي</option>
            </select>
          </div>
          <Field name="prepared_by" label="أعده" />
          <Field name="reviewed_by" label="راجعه" />
          <Field name="approved_by" label="اعتمده" />
        </QuickForm>
        <QuickForm title="بيانات الفاتورة" action={`${path}/invoice-record`} onSubmit={submitJson}>
          <Field name="organization_name" label="اسم الجهة" defaultValue={request.organization_name} />
          <Field name="service_description" label="وصف الخدمة" textarea />
          <Field name="amount_before_vat" label="المبلغ قبل الضريبة" type="number" />
          <Field name="vat_amount" label="الضريبة" type="number" />
          <Field name="total_amount" label="الإجمالي" type="number" />
          <Field name="payment_status" label="حالة السداد" />
          <Field name="official_invoice_no_from_accounting_system" label="رقم الفاتورة من النظام المحاسبي" />
        </QuickForm>
      </div>

      <Panel title="السجلات المرتبطة">
        <SummaryList
          title="عروض السعر"
          rows={quotations.map((row) => (
            <a className="font-bold text-charcoal hover:text-gold" href={`/api/admin/print/quotation/${row.id}`}>
              {row.quotation_no} - {row.status} - {row.total_amount}
            </a>
          ))}
        />
        <SummaryList title="أوامر العمل" rows={workOrders.map((row) => `${row.work_order_no} - ${row.status} - ${row.assigned_owner}`)} />
        <SummaryList title="المعاينات" rows={inspections.map((row) => `${row.inspection_date} - ${row.inspector_name}`)} />
        <SummaryList
          title="التقارير"
          rows={reports.map((row) => (
            <a className="font-bold text-charcoal hover:text-gold" href={`/api/admin/print/report/${row.id}`}>
              {row.report_no} - {row.report_type} - {row.status}
            </a>
          ))}
        />
        <SummaryList title="بيانات الفاتورة" rows={invoiceRecords.map((row) => `${row.total_amount} - ${row.payment_status}`)} />
        <SummaryList title="سجل الحالة" rows={statusHistory.map((row) => `${row.old_status} -> ${row.new_status} - ${row.actor_email} - ${row.created_at}`)} />
      </Panel>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-line bg-white p-6 shadow-card">
      <h1 className="mb-5 text-2xl font-bold text-charcoal">{title}</h1>
      {children}
    </section>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line p-4">
      <span className="text-sm font-bold text-gold">{label}</span>
      <strong className="mt-2 block leading-8 text-charcoal">{value}</strong>
    </div>
  );
}

function Field({
  name,
  label,
  type = "text",
  textarea = false,
  defaultValue,
}: {
  name: string;
  label: string;
  type?: string;
  textarea?: boolean;
  defaultValue?: string;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {textarea ? (
        <textarea name={name} rows={3} defaultValue={defaultValue} required />
      ) : (
        <input name={name} type={type} defaultValue={defaultValue} required />
      )}
    </div>
  );
}

function QuickForm({
  title,
  action,
  onSubmit,
  children,
}: {
  title: string;
  action: string;
  onSubmit: (route: string, form: HTMLFormElement) => Promise<void>;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-line bg-white p-6 shadow-card">
      <h2 className="mb-4 text-xl font-bold text-charcoal">{title}</h2>
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit(action, event.currentTarget);
        }}
      >
        {children}
        <button className="btn btn-primary" type="submit">حفظ</button>
      </form>
    </section>
  );
}

function SummaryList({ title, rows }: { title: string; rows: ReactNode[] }) {
  return (
    <div className="mb-5">
      <h2 className="mb-2 text-lg font-bold text-charcoal">{title}</h2>
      {rows.length ? (
        <ul className="grid gap-2 text-sm text-graphite">
          {rows.map((row, index) => (
            <li key={index} className="rounded-md bg-sand p-3">{row}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ash">لا توجد سجلات.</p>
      )}
    </div>
  );
}
